import cn from "classnames";
import { AppContext } from "components/App/AppContext";
import { EquipmentBlockContext } from "components/utils/contexts";
import { useCallback, useContext, useMemo, useState } from "react";
import { EquipmentAdd } from "./EquipmentAdd";
import { EquipmentEdit } from "./EquipmentEdit";
import { EquipmentList } from "./EquipmentList";
import { EquipmentAddSubmitItem } from "types/EquipmentAddSubmitItem";
import { EquipmentListItem } from "types/EquipmentListItem";
import { EquipmentEditSubmitItem } from "types/EquipmentEditSubmitItem";
import { EquipmentBlockContextValue } from "types/EquipmentBlockContextValue";
import { refreshEquipmentList } from "./useEquipmentList";
import { createEquipment, deleteEquipment, updateEquipment } from "./utils";
import { EquipmentCopy } from "./EquipmentCopy";
import { useToast } from "components/Toast";
import { Button } from "components/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useMediaQuery from "components/utils/useMediaQuery";
import { getErrorMessage } from "components/utils/http";
import { Heading } from "components/Heading";
import { HeadingLevel } from "types/HeadingLevel";

import "./style.scss";

export const EquipmentBlock: React.FC<{
    className?: string;
    tableClassName?: string;
    headerClassName?: string;
    mobileTableRowClassName?: string;
    mobileRowItemClassName?: string;
    mobileActionsClassName?: string;
    applicationNumber: string;
    readOnly: boolean;
    sectionHeadingLevel: HeadingLevel;
    tableStyle?: React.CSSProperties;
}> = ({
    className,
    applicationNumber,
    readOnly,
    sectionHeadingLevel,
    tableClassName,
    headerClassName,
    mobileTableRowClassName,
    mobileRowItemClassName,
    mobileActionsClassName,
    tableStyle,
}) => {
    const [activeEquipment, setActiveEquipment] = useState<EquipmentListItem>();
    const [activeCopyEquipment, setActiveCopyEquipment] = useState<EquipmentListItem>();
    const [showEquipmentAdd, setShowEquipmentAdd] = useState<boolean>(false);

    const { requirements } = useContext(AppContext);
    const toast = useToast();
    const isMobile = useMediaQuery("(max-width: 768px)");

    const allowEquipmentEntry = !readOnly && requirements.allowEquipmentEntry;

    const onEquipmentAdd = useCallback(() => {
        setShowEquipmentAdd(true);
    }, []);

    const onEquipmentAddSubmit = useCallback(
        async (equipmentItem: EquipmentAddSubmitItem) => {
            const response = await createEquipment(applicationNumber, equipmentItem);
            setShowEquipmentAdd(false);
            toast.success(response.responseMessage);
            refreshEquipmentList(applicationNumber);
        },
        [applicationNumber, toast]
    );

    const onEquipmentCopySubmit = useCallback(
        async (equipmentItem: EquipmentEditSubmitItem, hasApprovedMeasure?: boolean) => {
            const response = await createEquipment(applicationNumber, equipmentItem);
            setActiveCopyEquipment(undefined);
            toast.success(hasApprovedMeasure ? "Approved measure successfully created" : response.responseMessage);
            refreshEquipmentList(applicationNumber);
        },
        [applicationNumber, toast]
    );

    const onEquipmentEditSubmit = useCallback(
        async (equipmentNumber: string, equipmentItem: EquipmentEditSubmitItem, hasApprovedMeasure?: boolean) => {
            const response = await updateEquipment(applicationNumber, equipmentNumber, equipmentItem);
            setActiveEquipment(undefined);
            toast.success(hasApprovedMeasure ? "Approved measure successfully updated" : response.responseMessage);
            refreshEquipmentList(applicationNumber);
        },
        [applicationNumber, toast]
    );

    const onEquipmentAddClose = useCallback(() => {
        setShowEquipmentAdd(false);
        refreshEquipmentList(applicationNumber);
    }, [applicationNumber]);

    const onEquipmentDelete = useCallback(
        async (equipmentNumber: string, hasApprovedMeasure?: boolean) => {
            try {
                const response = await deleteEquipment(applicationNumber, equipmentNumber);
                toast.success(hasApprovedMeasure ? "Approved measure successfully deleted" : response.responseMessage);
            } catch (error) {
                toast.error(getErrorMessage(error));
            } finally {
                refreshEquipmentList(applicationNumber);
            }
        },
        [applicationNumber, toast]
    );

    const contextValue: EquipmentBlockContextValue = useMemo(
        () => ({
            applicationNumber,
            onEquipmentAdd,
            onEquipmentAddSubmit,
            onEquipmentAddClose,
            onEquipmentEdit: setActiveEquipment,
            onEquipmentEditSubmit,
            onEquipmentEditClose: () => setActiveEquipment(undefined),
            onEquipmentCopy: setActiveCopyEquipment,
            onEquipmentCopySubmit,
            onEquipmentCopyClose: () => setActiveCopyEquipment(undefined),
            onEquipmentDelete,
        }),
        [
            applicationNumber,
            onEquipmentAdd,
            onEquipmentAddSubmit,
            onEquipmentAddClose,
            onEquipmentEditSubmit,
            onEquipmentCopySubmit,
            onEquipmentDelete,
        ]
    );

    return (
        <EquipmentBlockContext.Provider value={contextValue}>
            <div className={cn("equipment-block d-flex flex-column gap-3", className)}>
                <div className={cn("d-flex justify-content-between gap-3", headerClassName, { "flex-column": isMobile })}>
                    <Heading level={sectionHeadingLevel} className="m-0">
                        Equipment
                    </Heading>
                    {allowEquipmentEntry && <AddEquipmentButton onClick={onEquipmentAdd} />}
                </div>
                <EquipmentList
                    readOnly={readOnly}
                    tableClassName={tableClassName}
                    tableStyle={tableStyle}
                    mobileTableRowClassName={mobileTableRowClassName}
                    mobileRowItemClassName={mobileRowItemClassName}
                    mobileActionsClassName={mobileActionsClassName}
                />
                {showEquipmentAdd && <EquipmentAdd />}
                {activeEquipment && <EquipmentEdit item={activeEquipment} readonly={!allowEquipmentEntry} />}
                {activeCopyEquipment && <EquipmentCopy item={activeCopyEquipment} />}
            </div>
        </EquipmentBlockContext.Provider>
    );
};

const AddEquipmentButton: React.FC<{
    onClick: () => void;
}> = ({ onClick }) => {
    return (
        <Button
            className="align-self-start d-flex align-items-center gap-2 px-3p5 py-2 justify-content-center text-nowrap"
            onClick={onClick}
            aria-haspopup="dialog"
        >
            <FontAwesomeIcon icon={["fal", "square-plus"]} />
            Add Equipment
        </Button>
    );
};
